import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatPriceFR } from '../utils/formatFR';

const API = process.env.REACT_APP_BACKEND_URL;

const AvisDeValeur = () => {
  // États pour le formulaire
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  
  // États pour les données du bien
  const [surfaceTotale, setSurfaceTotale] = useState('');
  const [surfacePonderee, setSurfacePonderee] = useState('');
  const [etatLocaux, setEtatLocaux] = useState('Bon état d\'usage');
  
  // États pour Valeur Vénale
  const [typeActivite, setTypeActivite] = useState('Fonds Commerce');
  const [caHT, setCaHT] = useState('');
  const [baremePercent, setBaremePercent] = useState('');
  const [ebeRetraite, setEbeRetraite] = useState('');
  const [multiple, setMultiple] = useState('');
  const [valeurVenale, setValeurVenale] = useState(0);
  
  // États pour Valeur Locative & IA complète
  const [valeurLocative, setValeurLocative] = useState(0);
  const [fluxPieton, setFluxPieton] = useState('--');
  const [loyerQuartier, setLoyerQuartier] = useState('--');
  const [iaActive, setIaActive] = useState(false);
  const [iaEstimation, setIaEstimation] = useState(null);  // objet complet de GPT
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Charger la liste des biens au montage
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeProperties = response.data.filter(p => p.statut === 'active' || p.status === 'active' || !p.status);
      setProperties(activeProperties);
      setFilteredProperties(activeProperties);
    } catch (error) {
      console.error('Erreur chargement biens:', error);
    }
  };

  // Filtrer les biens en fonction de la recherche
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProperties(properties);
      setShowDropdown(false);
    } else {
      const filtered = properties.filter(p => {
        const mandat = (p.numero_mandat || '').toLowerCase();
        const activite = (p.bien_general?.activite_principale || '').toLowerCase();
        const adresse = (p.bien_general?.adresse || '').toLowerCase();
        const ville = (p.bien_general?.ville || '').toLowerCase();
        const search = searchQuery.toLowerCase();
        
        return mandat.includes(search) || 
               activite.includes(search) || 
               adresse.includes(search) ||
               ville.includes(search);
      });
      setFilteredProperties(filtered);
      setShowDropdown(filtered.length > 0);
    }
  }, [searchQuery, properties]);

  // Sélectionner un bien depuis l'autocomplete
  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setSearchQuery(`${property.numero_mandat || property.id.substring(0, 8)} - ${property.bien_general?.activite_principale || 'Bien'}`);
    setShowDropdown(false);
    
    // Remplir automatiquement les champs
    setSurfaceTotale(property.composition?.superficie_totale || '');
    setSurfacePonderee(property.composition?.superficie_ponderee || property.composition?.superficie_totale || '');
    
    // Remplir autres champs si disponibles
    if (property.honoraires_calcul?.prix_vente) {
      // On peut pré-remplir certaines valeurs
    }
  };

  // Extraire ville depuis la sélection ou l'adresse manuelle
  const extractVille = () => {
    if (selectedProperty) {
      return selectedProperty.informations_confidentielles?.ville
          || selectedProperty.bien_general?.ville
          || '';
    }
    // tenter extraction depuis manualAddress : last segment après virgule
    if (manualAddress) {
      const parts = manualAddress.split(',').map(s => s.trim()).filter(Boolean);
      return parts[parts.length - 1] || '';
    }
    return '';
  };

  // Générer le dossier PDF (nouveau flux : estimate → save → download)
  const genererDossier = async () => {
    if (generating) return;

    if (!surfaceTotale || !extractVille()) {
      showToast('Remplissez au minimum Surface totale et Ville (ou sélectionnez un bien)', 'error');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      // 1) S'assurer qu'on a une estimation IA (si pas déjà lancée)
      let estim = iaEstimation;
      if (!estim) {
        estim = await runEstimationIA(true);  // silencieux (pas de toast)
      }

      // 2) Sauvegarder
      const payload = {
        property_id: selectedProperty?.id || null,
        adresse_libre: manualAddress || null,
        ville: extractVille(),
        type_activite: typeActivite,
        profil_commerce: selectedProperty?.bien_general?.activite_principale || null,
        surface_totale: parseFloat(surfaceTotale) || 0,
        surface_ponderee: parseFloat(surfacePonderee) || parseFloat(surfaceTotale) || 0,
        etat_locaux: etatLocaux,
        ca_ht: parseFloat(caHT) || null,
        ebe_retraite: parseFloat(ebeRetraite) || null,
        bareme_percent: parseFloat(baremePercent) || null,
        multiple: parseFloat(multiple) || null,
        valeur_venale: valeurVenale,
        valeur_locative: valeurLocative || estim?.valeur_locative_annuelle || 0,
        methode_valorisation: caHT && baremePercent ? 'CA × Barème' : (ebeRetraite && multiple ? 'EBE × Multiple' : 'Analyse IA'),
        ia_estimation: estim,
      };

      const { data: saved } = await axios.post(`${API}/api/avis-valeur/save`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 3) Télécharger le PDF
      const pdfResponse = await axios.get(`${API}/api/avis-valeur/${saved.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avis_valeur_${saved.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showToast('✓ Avis de valeur sauvegardé et PDF téléchargé');
    } catch (error) {
      console.error('Erreur génération dossier:', error);
      showToast(error.response?.data?.detail || 'Erreur lors de la génération du dossier', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Calcul automatique de la Valeur Vénale
  useEffect(() => {
    let estimation = 0;
    
    if (typeActivite === 'Fonds Commerce' && caHT && baremePercent) {
      // Méthode CA × Barème
      estimation = (parseFloat(caHT) * parseFloat(baremePercent)) / 100;
    } else if (typeActivite === 'Fonds Commerce' && ebeRetraite && multiple) {
      // Méthode EBE × Multiple
      estimation = parseFloat(ebeRetraite) * parseFloat(multiple);
    }
    
    setValeurVenale(estimation);
  }, [typeActivite, caHT, baremePercent, ebeRetraite, multiple]);

  // Estimation IA complète (GPT-5.2) : loyer, flux piéton, zone, synthèse
  const runEstimationIA = async (silent = false) => {
    const ville = extractVille();
    if (!ville) {
      if (!silent) showToast('Sélectionnez un bien ou saisissez une adresse avec une ville', 'error');
      return null;
    }
    if (!silent) setIaActive(true);
    setFluxPieton('Analyse IA…');
    setLoyerQuartier('Analyse IA…');

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/api/avis-valeur/estimate`, {
        ville,
        adresse: manualAddress || selectedProperty?.informations_confidentielles?.adresse || selectedProperty?.bien_general?.adresse || '',
        type_activite: typeActivite,
        profil_commerce: selectedProperty?.bien_general?.activite_principale || '',
        surface_totale: parseFloat(surfaceTotale) || 0,
        surface_ponderee: parseFloat(surfacePonderee) || parseFloat(surfaceTotale) || 0,
        etat_locaux: etatLocaux,
        ca_ht: parseFloat(caHT) || null,
        ebe_retraite: parseFloat(ebeRetraite) || null,
        valeur_venale: valeurVenale,
        methode_valorisation: caHT && baremePercent ? 'CA × Barème' : (ebeRetraite && multiple ? 'EBE × Multiple' : null),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setIaEstimation(data);
      setValeurLocative(data.valeur_locative_annuelle || 0);
      setFluxPieton(data.flux_pieton || '--');
      setLoyerQuartier(data.loyer_m2_annuel ? `${data.loyer_m2_annuel} €/m²/an` : '--');
      if (!silent) showToast('✓ Estimation IA terminée');
      return data;
    } catch (error) {
      console.error('Erreur estimation IA:', error);
      const d = error.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Erreur lors de l\'estimation IA');
      if (!silent) showToast(msg, 'error');
      setFluxPieton('--');
      setLoyerQuartier('--');
      return null;
    } finally {
      if (!silent) setIaActive(false);
    }
  };

  const lancerEstimationIA = () => runEstimationIA(false);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      {/* Toast */}
      {toast && (
        <div
          data-testid="avis-toast"
          className={`fixed top-6 right-6 z-[120] px-4 py-3 rounded-lg shadow-2xl text-sm font-medium max-w-sm ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header avec bouton Générer */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl shadow-lg p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-white">
          <h1 className="text-2xl font-bold mb-1">Avis de Valeur</h1>
          <p className="text-slate-300 text-sm">Estimation Valeur Vénale &amp; Locative — Propulsé par l'IA Komerces</p>
        </div>
        <button 
          data-testid="avis-generate-btn"
          onClick={genererDossier}
          disabled={generating}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl"
        >
          <i className={`bi ${generating ? 'bi-arrow-repeat animate-spin' : 'bi-file-earmark-pdf'} text-xl`}></i>
          <span>{generating ? 'Génération en cours…' : 'Générer le Dossier'}</span>
        </button>
      </div>

      {/* Section Identification du Bien */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
        <h2 className="font-bold text-slate-700 mb-4 text-sm uppercase tracking-wide border-b border-slate-100 pb-2">
          1. Identification du Bien
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Recherche dans le stock avec autocomplete */}
          <div className="relative">
            <label className="block text-xs font-bold text-blue-600 mb-2">
              <i className="bi bi-box mr-1"></i> Rechercher un bien du Stock
            </label>
            <div className="relative">
              <input 
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowDropdown(true)}
                placeholder="Tapez un mandat, activité, adresse..."
                className="w-full bg-blue-50/50 border border-blue-200 rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700"
              />
              <i className="bi bi-search absolute right-3 top-1/2 -translate-y-1/2 text-blue-400"></i>
              
              {/* Dropdown autocomplete */}
              {showDropdown && filteredProperties.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredProperties.map(prop => (
                    <button
                      key={prop.id}
                      onClick={() => handlePropertySelect(prop)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 transition text-sm border-b border-slate-100 last:border-0"
                    >
                      <div className="font-semibold text-slate-800">
                        {prop.numero_mandat || prop.id.substring(0, 8)} - {prop.bien_general?.activite_principale || 'Bien'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {prop.bien_general?.adresse}, {prop.bien_general?.ville} • {prop.composition?.superficie_totale || '?'}m²
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1 ml-1">
              Les infos (Surface, Activité) seront remplies automatiquement.
            </p>
          </div>

          {/* Nouvelle adresse */}
          <div className="relative">
            <div className="absolute left-0 -ml-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-400 z-10 hidden md:flex">
              OU
            </div>
            
            <label className="block text-xs font-bold text-purple-600 mb-2">
              <i className="bi bi-magic mr-1"></i> Nouvelle Adresse (Recherche IA)
            </label>
            <div className="relative">
              <input 
                type="text" 
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                placeholder="Ex: 12 Rue de la République, Nice..." 
                className="w-full pl-3 pr-10 bg-purple-50/50 border border-purple-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <i className="bi bi-search absolute right-3 top-1/2 -translate-y-1/2 text-purple-400"></i>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 ml-1">
              L'IA analysera le flux piéton et l'environnement.
            </p>
          </div>
        </div>

        {/* Informations du bien */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 mt-6 pt-6 border-t border-slate-100">
          <div className="lg:col-span-3">
            <label className="block text-xs font-bold text-slate-500 mb-1">Surface Totale (m²)</label>
            <input 
              type="number" 
              value={surfaceTotale}
              onChange={(e) => setSurfaceTotale(e.target.value)}
              placeholder="0" 
              className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:border-blue-500"
            />
          </div>
          <div className="lg:col-span-3">
            <label className="block text-xs font-bold text-slate-500 mb-1">Surface Pondérée (m²)</label>
            <input 
              type="number" 
              value={surfacePonderee}
              onChange={(e) => setSurfacePonderee(e.target.value)}
              placeholder="0" 
              className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-500"
            />
          </div>
          <div className="lg:col-span-6">
            <label className="block text-xs font-bold text-slate-500 mb-1">État des locaux</label>
            <select 
              value={etatLocaux}
              onChange={(e) => setEtatLocaux(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm outline-none"
            >
              <option>Excellent état</option>
              <option>Bon état d'usage</option>
              <option>Travaux à prévoir</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid Valeur Vénale et Locative */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Valeur Vénale */}
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-800 text-lg">
              <i className="bi bi-shop mr-2 text-blue-500"></i> Valeur Vénale
            </h2>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold">Vente</span>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex-1">
            {/* Type d'activité */}
            <div className="mb-6">
              <label className="block text-xs font-bold text-slate-500 mb-2">Type d'Activité / Cession</label>
              <div className="grid grid-cols-2 gap-2">
                {['Fonds Commerce', 'Murs Commerciaux', 'Droit au Bail (DAB)', 'Pas de Porte'].map((type) => (
                  <label key={type} className="cursor-pointer">
                    <input 
                      type="radio" 
                      name="type" 
                      value={type}
                      checked={typeActivite === type}
                      onChange={(e) => setTypeActivite(e.target.value)}
                      className="peer sr-only" 
                    />
                    <div className="py-2 px-3 rounded border border-slate-200 text-center text-xs font-bold text-slate-600 peer-checked:bg-slate-800 peer-checked:text-white peer-checked:border-slate-800 transition">
                      {type}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Méthodes de calcul */}
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">C.A. HT (Moyenne)</label>
                  <div className="flex items-center bg-slate-50 border border-slate-200 rounded px-3 py-2">
                    <input 
                      type="number" 
                      value={caHT}
                      onChange={(e) => setCaHT(e.target.value)}
                      className="bg-transparent w-full outline-none text-sm font-bold text-slate-700" 
                      placeholder="0"
                    />
                    <span className="text-slate-400 text-xs">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Barème (%)</label>
                  <input 
                    type="number" 
                    value={baremePercent}
                    onChange={(e) => setBaremePercent(e.target.value)}
                    placeholder="Ex: 80" 
                    className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm outline-none text-center"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">EBE Retraité</label>
                  <div className="flex items-center bg-slate-50 border border-slate-200 rounded px-3 py-2">
                    <input 
                      type="number" 
                      value={ebeRetraite}
                      onChange={(e) => setEbeRetraite(e.target.value)}
                      className="bg-transparent w-full outline-none text-sm font-bold text-slate-700" 
                      placeholder="0"
                    />
                    <span className="text-slate-400 text-xs">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Multiple (x)</label>
                  <input 
                    type="number" 
                    value={multiple}
                    onChange={(e) => setMultiple(e.target.value)}
                    placeholder="Ex: 3.5" 
                    className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm outline-none text-center"
                  />
                </div>
              </div>
            </div>

            {/* Résultat Valeur Vénale */}
            <div className="mt-8 bg-slate-100 rounded-lg p-4 border border-slate-200 text-center">
              <p className="text-xs text-slate-500 uppercase font-bold mb-1">Estimation Valeur Vénale</p>
              <p className="text-2xl font-bold text-slate-800 num">
                {valeurVenale > 0 ? formatPriceFR(valeurVenale) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Valeur Locative */}
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-800 text-lg flex items-center gap-2">
              <i className="bi bi-robot text-purple-600"></i> Valeur Locative
            </h2>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-bold border border-purple-200 flex items-center gap-1">
              <span className={`w-2 h-2 bg-purple-500 rounded-full ${iaActive ? 'animate-pulse' : ''}`}></span> 
              {iaActive ? 'IA en cours...' : 'IA Active'}
            </span>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6 relative overflow-hidden flex-1 flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>

            <div className="space-y-4 relative z-10">
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-2 rounded border border-slate-100">
                <i className="bi bi-rulers"></i>
                Calcul basé sur la surface pondérée ci-dessus.
              </div>

              <p className="text-[10px] uppercase font-bold text-purple-500 tracking-wider">Analyse de marché (IA)</p>
              
              <div className="flex items-center justify-between text-sm p-3 bg-purple-50 rounded border border-purple-100">
                <span className="text-slate-600">
                  <i className="bi bi-people mr-2 text-purple-400"></i>Flux Piéton
                </span>
                <span className="font-bold text-slate-800">{fluxPieton}</span>
              </div>
              <div className="flex items-center justify-between text-sm p-3 bg-purple-50 rounded border border-purple-100">
                <span className="text-slate-600">
                  <i className="bi bi-currency-euro mr-2 text-purple-400"></i>Loyers quartier
                </span>
                <span className="font-bold text-slate-800">{loyerQuartier}</span>
              </div>
            </div>

            {/* Résultat Valeur Locative */}
            <div className="bg-slate-900 rounded-lg p-5 text-center text-white relative overflow-hidden mt-6">
              <div className="relative z-10">
                <p className="text-xs text-purple-300 uppercase font-bold mb-1">Estimation Valeur Locative (An)</p>
                <p className="text-3xl font-bold text-white mb-1 num" data-testid="avis-valeur-locative">
                  {valeurLocative > 0 ? formatPriceFR(valeurLocative) : '—'}
                </p>
                <button 
                  data-testid="avis-ia-btn"
                  onClick={lancerEstimationIA}
                  disabled={iaActive || (!surfacePonderee && !surfaceTotale)}
                  className="mt-2 text-[10px] bg-purple-600 hover:bg-purple-500 text-white px-3 py-1 rounded font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {iaActive ? 'Analyse IA en cours…' : 'Lancer l\'estimation IA'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ Synthèse IA (affichée après génération) ═══ */}
        {iaEstimation && iaEstimation.synthese_commentaire && (
          <div className="mt-6 pt-6 border-t border-slate-100" data-testid="avis-synthese">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-700 flex items-center justify-center">
                <i className="bi bi-stars text-white text-sm"></i>
              </div>
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Synthèse IA professionnelle</h3>
              <span className="text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-bold ml-2">Komerces IA</span>
              {iaEstimation.niveau_confiance && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ml-auto ${
                  iaEstimation.niveau_confiance === 'Élevé' ? 'bg-emerald-100 text-emerald-700' :
                  iaEstimation.niveau_confiance === 'Faible' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  Confiance : {iaEstimation.niveau_confiance}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <p className="text-[10px] font-bold text-violet-600 uppercase mb-1">Loyer moyen zone</p>
                <p className="text-lg font-bold text-slate-800">{iaEstimation.loyer_m2_annuel} €/m²/an</p>
                <p className="text-[11px] text-slate-500">
                  Fourchette : {iaEstimation.loyer_m2_min} → {iaEstimation.loyer_m2_max} €/m²
                </p>
              </div>
              <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <p className="text-[10px] font-bold text-violet-600 uppercase mb-1">Fourchette retenue</p>
                <p className="text-lg font-bold text-slate-800 num">
                  {formatPriceFR(iaEstimation.fourchette_basse)} → {formatPriceFR(iaEstimation.fourchette_haute)}
                </p>
                <p className="text-[11px] text-slate-500">Valeur vénale recommandée</p>
              </div>
              <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <p className="text-[10px] font-bold text-violet-600 uppercase mb-1">Contexte zone</p>
                <p className="text-xs text-slate-700 leading-snug">{iaEstimation.zone_commentaire}</p>
              </div>
            </div>
            <div className="bg-slate-50 border-l-4 border-violet-500 rounded-r-lg p-4">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {iaEstimation.synthese_commentaire}
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AvisDeValeur;
